import * as React from "react"

import { cn } from "@/lib/utils"
import { capitalizeWords, TEXTUAL_INPUT_TYPES } from "@/lib/autoCap"

/**
 * Input — globally auto-capitalises the first letter of every word by
 * default. Two escape hatches so the transform never gets in the user's way:
 *
 *   1. Hold **Shift** while typing a character → that keystroke is left
 *      untouched (matches the mobile-keyboard mental model users know).
 *   2. Turn on **Caps Lock** → the browser is already producing uppercase
 *      characters, so we skip re-transforming and let the whole field
 *      stay uppercase per the user's intent.
 *
 * The transform is automatically bypassed for non-textual input types
 * (email / password / number / url / tel / search / date / …) so login
 * addresses, GSTIN case toggles, OTPs and dates are never touched.
 * Callers can also opt out per-field via `autoCap={false}`.
 */
const Input = React.forwardRef(
  ({ className, type, autoCap = true, onChange, onKeyDown, ...props }, ref) => {
    // If the input isn't a plain-text-ish type, we never auto-capitalise
    // (email addresses, dates, passwords etc. must stay as the user typed).
    const active = autoCap && (!type || TEXTUAL_INPUT_TYPES.has(type))

    const capsRef = React.useRef(false)
    const shiftRef = React.useRef(false)

    const handleKeyDown = (e) => {
      if (typeof e.getModifierState === "function") {
        capsRef.current = e.getModifierState("CapsLock")
      }
      // Track Shift for THIS one keystroke — reset after the following
      // change fires so subsequent characters get auto-cap again.
      shiftRef.current = !!e.shiftKey
      onKeyDown?.(e)
    }

    const handleChange = (e) => {
      if (active && !capsRef.current && !shiftRef.current) {
        const next = capitalizeWords(e.target.value)
        if (next !== e.target.value) {
          // Length is identical (only uppercasing existing chars), so the
          // caret naturally stays in the right place.
          e.target.value = next
        }
      }
      shiftRef.current = false
      onChange?.(e)
    }

    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        onChange={active ? handleChange : onChange}
        onKeyDown={active ? handleKeyDown : onKeyDown}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
